import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, pick, shuffle, splitFace, useGame, useRoundTimer, type GameProps } from '../sdk/react';
import { KINDS } from '../../concepts/kinds';
import { Pic } from '../sdk/react';

type Item = { say: string; answer: string };

export default function YesNo(props: GameProps) {
  const game = useGame(props);
  const items = ((game.config?.items ?? []) as Item[]).filter((i) => i?.say && ['yes', 'no'].includes(String(i.answer).trim().toLowerCase()));
  const [item, setItem] = useState<Item | null>(null);
  const [trial, setTrial] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const queue = useRef<Item[]>([]);
  const busy = useRef(false);
  const fixed = game.str('trialOrder', 'random') === 'fixed';

  const start = useCallback(async () => {
    if (items.length === 0) return;
    if (queue.current.length === 0) queue.current = fixed ? [...items] : shuffle(items);
    const next = queue.current.shift() ?? pick(items);
    setDone(false);
    setChosen(null);
    const id = await game.register({ question: next.say }, { value: next.answer.trim().toLowerCase() });
    if (!id) return;
    setItem(next);
    setTrial(id);
  }, [items.map((i) => i.say + i.answer).join('|'), fixed]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (!item || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(item.say), 300);
    return () => window.clearTimeout(t);
  }, [item, trial]);

  async function answer(a: 'yes' | 'no') {
    if (!item || !trial || done || busy.current) return;
    const right = item.answer.trim().toLowerCase();
    if (game.errorless && a !== right) return;
    busy.current = true;
    const v = await game.respond(trial, a);
    if (v?.correct) { setChosen(a); setDone(true); busy.current = false; return; }
    game.say(item.say);
    const fresh = await game.register({ question: item.say, retry: true }, { value: right });
    busy.current = false;
    if (fresh) setTrial(fresh);
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); });
  const face = item ? splitFace(item.say) : null;

  return (
    <GameFrame game={game} empty={items.length === 0} emptyText={`${KINDS.yesNo.describes} Edit it to add questions.`} onResume={() => { void start(); }} breakTrial={trial}>
      {item && face && (
        <div className={`g-board g-yesno${fading ? ' is-fading' : ''}`}>
          <Pic text={item.say} fallback={face.picture ? <div className="g-hero" aria-hidden="true">{face.picture}</div> : null} className="g-hero-img" />
          <div className="g-prompt" aria-live="polite">
            <span className="g-prompt-text">{face.word}</span>
            {!game.textPrompt && (
              <button type="button" className="g-say" aria-label="Say it again" onClick={() => game.say(item.say)}><span aria-hidden="true">🔊</span></button>
            )}
          </div>
          <div className="g-yesno-row">
            {(['yes', 'no'] as const).map((a) => (
              <button
                key={a}
                type="button"
                className={`tile g-yn is-${a}${chosen === a ? ' correct' : ''}`}
                onClick={() => void answer(a)}
                disabled={done}
              >
                <span className="g-yn-mark" aria-hidden="true">{a === 'yes' ? '✓' : '✗'}</span>
                <span>{a === 'yes' ? 'Yes' : 'No'}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </GameFrame>
  );
}
